import { FC } from "react";
import { t } from "../../i18n/ar";

// ─── Pick-list ordering shared by the Jumia and Marjan views ────────────────
//   time  — oldest waiting order first (default: old orders ship first)
//   place — shelf walk order (one pass through the aisles)
//   size  — biggest items first (choose the carton, load the trolley bottom-up)
//   qty   — most pieces to take first
// Fully-taken products always sink to the end whatever the mode.
export type PickSort = "time" | "place" | "size" | "qty";

const SIZE_RANK: Record<string, number> = { XXL: 0, XL: 1, L: 2, M: 3, S: 4 };

interface PickView {
    left: number;
    oldestUndone: string | null;
    product: { walk?: Array<string | number>; sizeClass?: string | null; totalQty: number; name?: string };
}

export const loadPickSort = (key: string): PickSort => {
    try {
        const v = localStorage.getItem(key);
        if (v === "place" || v === "size" || v === "qty" || v === "time") return v;
        if (localStorage.getItem("packing.byPlace") === "1") return "place"; // legacy toggle
    } catch {}
    return "time";
};
export const savePickSort = (key: string, v: PickSort) => {
    try { localStorage.setItem(key, v); } catch {}
};

export const comparePick = (mode: PickSort) => (a: PickView, b: PickView): number => {
    const aDone = a.left === 0 ? 1 : 0;
    const bDone = b.left === 0 ? 1 : 0;
    if (aDone !== bDone) return aDone - bDone;
    const byTime = () => {
        const at = a.oldestUndone ? new Date(a.oldestUndone).getTime() : Infinity;
        const bt = b.oldestUndone ? new Date(b.oldestUndone).getTime() : Infinity;
        return at - bt;
    };
    const byQty = () => b.product.totalQty - a.product.totalQty;
    if (mode === "place") {
        const wa = a.product.walk || [9, "~", 999, 9];
        const wb = b.product.walk || [9, "~", 999, 9];
        for (let i = 0; i < wa.length; i++) {
            if (wa[i] < wb[i]) return -1;
            if (wa[i] > wb[i]) return 1;
        }
        return byTime() || byQty();
    }
    if (mode === "size") {
        const ra = SIZE_RANK[a.product.sizeClass || ""] ?? 9;
        const rb = SIZE_RANK[b.product.sizeClass || ""] ?? 9;
        if (ra !== rb) return ra - rb;
        return byTime() || byQty();
    }
    if (mode === "qty") return byQty() || byTime();
    return byTime() || byQty();
};

const MODES: Array<{ key: PickSort; icon: string; label: () => string }> = [
    { key: "time", icon: "🕐", label: () => t.sortByTime },
    { key: "place", icon: "📍", label: () => t.sortByPlace },
    { key: "size", icon: "📐", label: () => t.sortBySize },
    { key: "qty", icon: "🔢", label: () => t.sortByQty },
];

// Four big buttons; the active one is dark. Same look in both views.
export const PickSortBar: FC<{ value: PickSort; onChange: (v: PickSort) => void; accent?: string }> = ({ value, onChange, accent = "#0f172a" }) => (
    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {MODES.map((m) => (
            <button
                key={m.key}
                onClick={() => onChange(m.key)}
                style={value === m.key ? { backgroundColor: accent, borderColor: accent } : undefined}
                className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2 text-lg font-black transition-colors ${
                    value === m.key ? "text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                }`}
            >
                <span aria-hidden>{m.icon}</span> {m.label()}
            </button>
        ))}
    </div>
);
